import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function Login() {
    const { login, register, user } = useAuth();
    const nav = useNavigate();
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) {
        // Admin users (or anyone forced to change password) bypass the
        // user-facing dashboard entirely.
        if (user.must_change_password) return <Navigate to="/change-password" replace />;
        if (user.is_admin) return <Navigate to="/admin" replace />;
        if (user.status && user.status !== "active") return <Navigate to="/pending" replace />;
        return <Navigate to="/dashboard" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            let u;
            if (mode === "login") {
                u = await login(email, password);
                toast.success("Welcome back.");
            } else {
                u = await register(email, password, name || email);
                toast.success("Account created.");
            }
            // Route by user kind so admins land directly on /admin.
            if (u?.must_change_password)        nav("/change-password");
            else if (u?.is_admin)               nav("/admin");
            else if (u?.status && u.status !== "active") nav("/pending");
            else                                nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Authentication failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen relative grid lg:grid-cols-2 bg-[#050505] text-zinc-100">
            {/* Left visual */}
            <div className="hidden lg:flex relative grain overflow-hidden border-r border-zinc-900 tile-bg">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/2617a430-f2b5-4854-8204-53ddcd4d7fe3/images/f1e6c4a2161a161c4e379ebb6afde8d36259a7b69aa0e4d2af5b38280e903674.png)",
                        backgroundSize: "cover", backgroundPosition: "center"
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black" />
                <div className="relative flex flex-col justify-between p-12 z-10 w-full">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#FF4400] grid place-items-center rounded-sm">
                            <span className="font-display font-bold text-white">A</span>
                        </div>
                        <div className="font-display text-xl font-bold tracking-tight">FURMAIS</div>
                    </div>
                    <div>
                        <div className="label-micro mb-4">Creative Operating System</div>
                        <h1 className="font-display text-5xl xl:text-6xl font-medium leading-[1.05] tracking-tight">
                            Run your studio like a <span className="text-[#FF4400]">CEO</span>, not a chatbot.
                        </h1>
                        <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
                            One CEO Agent orchestrates Brand Analyst, Creative Director, Script,
                            Storyboard, Prompt Engineer, QC and Post-Production — end-to-end.
                        </p>
                        <div className="mt-8 flex items-center gap-2 text-xs text-zinc-500 font-mono">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> 10 SPECIALIST AGENTS
                            <span className="text-zinc-700">·</span> 13 STAGE WORKFLOW
                            <span className="text-zinc-700">·</span> SHAREABLE REVIEW LINKS
                        </div>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="auth-form">
                    <div>
                        <div className="label-micro mb-2">{mode === "login" ? "Sign in" : "Create an account"}</div>
                        <h2 className="font-display text-3xl font-medium tracking-tight">
                            {mode === "login" ? "Welcome back" : "Start producing"}
                        </h2>
                        <p className="text-sm text-zinc-500 mt-2">
                            {mode === "login"
                                ? "Sign in to your Furmais workspace."
                                : "Free tier — full agent suite included."}
                        </p>
                    </div>

                    {mode === "register" && (
                        <div>
                            <label className="label-micro block mb-2">Name</label>
                            <input
                                data-testid="register-name"
                                value={name} onChange={(e) => setName(e.target.value)}
                                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none"
                                placeholder="Studio name or your name"
                            />
                        </div>
                    )}

                    <div>
                        <label className="label-micro block mb-2">Email</label>
                        <input
                            data-testid="auth-email"
                            type="email" required
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none"
                        />
                    </div>
                    <div>
                        <label className="label-micro block mb-2">Password</label>
                        <input
                            data-testid="auth-password"
                            type="password" required
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none"
                        />
                    </div>

                    <button data-testid="auth-submit" disabled={busy} type="submit" className="btn-primary w-full py-2.5">
                        {busy ? "..." : mode === "login" ? "Sign in" : "Create account"}
                    </button>

                    <div className="text-xs text-zinc-500">
                        {mode === "login" ? (
                            <>No account? <Link to="/signup" data-testid="switch-register" className="text-white underline underline-offset-4">Create one</Link></>
                        ) : (
                            <>Already have one? <button type="button" data-testid="switch-login" onClick={() => setMode("login")} className="text-white underline underline-offset-4">Sign in</button></>
                        )}
                    </div>
                </form>
            </div>
            <Toaster theme="dark" />
        </div>
    );
}
